import {Component, Input, OnChanges, Output, EventEmitter, SimpleChanges} from '@angular/core';
import {CommonModule} from '@angular/common';
import {Dialog} from 'primeng/dialog';
import {FormBuilder, IMsfFormBuilder} from '@components/form-builder/form-builder';
import {PrimaryButton} from '@components/button/primary-button/primary-button';
import {TranslatePipe} from '@ngx-translate/core';
import {SecondaryButton} from '@components/button/secondary-button/secondary-button';

/** Target zoom after « go to coordinates » (zoom in if below, zoom out if above). */
export const GO_TO_COORDINATES_TARGET_ZOOM = 17;

/** Leaflet `setView` animation duration (seconds). Pan and zoom run together. */
const GO_TO_COORDINATES_ANIMATION_DURATION_S = 0.85;

declare var L: any;
@Component({
  selector: 'app-go-to-coordinates-modal',
  templateUrl: './go-to-coordinates-modal.component.html',
  styleUrls: ['./go-to-coordinates-modal.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    Dialog,
    FormBuilder,
    PrimaryButton,
    TranslatePipe,
    SecondaryButton,
  ]
})
export class GoToCoordinatesModalComponent implements OnChanges {
  @Input() visible: boolean = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Input() mapInstance: any;

  formModel: { lat: string | null; lng: string | null } = {
    lat: null,
    lng: null
  };

  private parseCoord(value: string | null): number | null {
    if (value === null || value === '') return null;
    const normalized = value.replace(',', '.');
    const parsed = parseFloat(normalized);
    return isNaN(parsed) ? null : parsed;
  }

  fields: IMsfFormBuilder[] = [
    {
      name: 'lat',
      type: 'input',
      title: 'Latitude',
      placeholder: 'Latitude',
      // You can add validation here if needed
    },
    {
      name: 'lng',
      type: 'input',
      title: 'Longitude',
      placeholder: 'Longitude',
      // You can add validation here if needed
    }
  ];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible']?.currentValue === true) {
      void this.prefillFromSystemClipboard();
    }
  }

  /** Try to parse lat/lng from the OS clipboard (presse-papiers / buffer admin). */
  private async prefillFromSystemClipboard(): Promise<void> {
    this.formModel = { lat: null, lng: null };
    if (!navigator?.clipboard?.readText) {
      return;
    }
    try {
      const text = (await navigator.clipboard.readText()).trim();
      const parsed = this.parseLatLngFromText(text);
      if (parsed) {
        this.formModel = {
          lat: String(parsed.lat),
          lng: String(parsed.lng),
        };
      }
    } catch {
      // Permission denied or unreadable clipboard — user enters coords manually.
    }
  }

  /** Extract two decimal coordinates from copied text (e.g. `55.7558, 37.6173`). */
  private parseLatLngFromText(text: string): { lat: number; lng: number } | null {
    if (!text) {
      return null;
    }
    const match = text.match(/(-?\d+(?:[.,]\d+)?)\s*[,;\s]\s*(-?\d+(?:[.,]\d+)?)/);
    if (!match) {
      return null;
    }
    const lat = parseFloat(match[1].replace(',', '.'));
    const lng = parseFloat(match[2].replace(',', '.'));
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return null;
    }
    if (Math.abs(lat) > 90 || Math.abs(lng) > 180) {
      return null;
    }
    return { lat, lng };
  }

  goToCoordinates(): void {
    const lat = this.parseCoord(this.formModel.lat);
    const lng = this.parseCoord(this.formModel.lng);

    if (!this.mapInstance || lat === null || lng === null) {
      return;
    }

    if (typeof this.mapInstance.setView === 'function') {
      // Single animated setView: pan + zoom to 17 finish together (zoom in or out as needed).
      this.mapInstance.setView([lat, lng], GO_TO_COORDINATES_TARGET_ZOOM, {
        animate: true,
        duration: GO_TO_COORDINATES_ANIMATION_DURATION_S,
        easeLinearity: 0.25,
      });
      this.addFlashMarker(lat, lng);
    } else if (typeof this.mapInstance.flyTo === 'function') {
      this.mapInstance.flyTo([lat, lng], GO_TO_COORDINATES_TARGET_ZOOM, {
        duration: GO_TO_COORDINATES_ANIMATION_DURATION_S,
      });
    } else if (typeof this.mapInstance.setCenter === 'function') {
      this.mapInstance.setCenter(lat, lng);
    }

    this.close();
  }

  private addFlashMarker(lat: number, lng: number) {
    if (!this.mapInstance) return;

    // Create a custom pulsating icon
    const pulsingIcon = L.divIcon({
      className: 'pulsing-marker',
      html: `<div class="pulse-ring"></div><div class="pulse-dot"></div>`,
      iconSize: [30, 30],
      iconAnchor: [15, 15]
    });

    // Add marker to map
    const marker = L.marker([lat, lng], { icon: pulsingIcon }).addTo(this.mapInstance);

    // Remove marker after 3 seconds
    setTimeout(() => {
      if (this.mapInstance && marker) {
        this.mapInstance.removeLayer(marker);
      }
    }, 5000);
  }

  close() {
    this.visible = false;
    this.visibleChange.emit(false);
  }
}
